/**
 * ChangeListPanel — collapsible left-side panel listing all diff changes.
 *
 * Shows numbered change entries with type, text summary, and decision status.
 * Clicking a row selects the change and scrolls the main view to it.
 * The panel scrolls independently; the selected row auto-scrolls into view.
 */
import { memo, useCallback, useEffect, useRef, useState } from 'react';

import type { ChangeResponse, ChangeType, DecisionStatus } from '../../lib/veridoc-api';
import type { ChangeStats } from './alignment';
import type { DecisionAdapter } from './decisionAdapter';
import type { NavigationAdapter } from './navigationAdapter';
import type { UseChangeListPanelResult } from './useChangeListPanel';

interface ChangeListPanelProps {
  changes: ChangeResponse[];
  allChangesCount: number;
  stats: ChangeStats;
  decisions?: DecisionAdapter;
  navigation: NavigationAdapter;
  panel: UseChangeListPanelResult;
  isFiltering: boolean;
  searchTerm: string;
  onSearchTermChange: (term: string) => void;
}

const CHANGE_TYPE_LABELS: Record<ChangeType, string> = {
  addition: '新增',
  deletion: '删除',
  modification: '修改',
  move: '移动',
  format: '格式',
};

const CHANGE_TYPE_BAR: Record<ChangeType, string> = {
  addition: 'bg-change-addition-fg',
  deletion: 'bg-change-deletion-fg',
  modification: 'bg-change-modification-fg',
  move: 'bg-change-move-fg',
  format: 'bg-change-format-fg',
};

function truncate(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength) + '…';
}

function HighlightedText({
  text,
  term,
}: {
  text: string;
  term: string;
}): React.JSX.Element {
  if (!term) return <>{text}</>;
  const lowerText = text.toLowerCase();
  const lowerTerm = term.toLowerCase();
  const idx = lowerText.indexOf(lowerTerm);
  if (idx === -1) return <>{text}</>;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="bg-brand-100 text-brand-900">{text.slice(idx, idx + term.length)}</mark>
      {text.slice(idx + term.length)}
    </>
  );
}

function PanelToggleStrip({
  onOpen,
}: {
  onOpen: () => void;
}): React.JSX.Element {
  return (
    <div
      role="button"
      tabIndex={0}
      aria-label="展开变更列表"
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') onOpen();
      }}
      className="flex h-full w-8 shrink-0 cursor-pointer flex-col items-center justify-center border-r border-ink-200 bg-ink-50 hover:bg-ink-100"
    >
      <span className="select-none text-sm text-ink-400" aria-hidden="true">
        ›
      </span>
    </div>
  );
}

export function ChangeListPanel({
  changes,
  allChangesCount,
  decisions,
  navigation,
  panel,
  isFiltering,
  searchTerm,
  onSearchTermChange,
}: ChangeListPanelProps): React.JSX.Element {
  const selectedRowRef = useRef<HTMLLIElement | null>(null);
  const [localSearch, setLocalSearch] = useState(searchTerm);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Scroll selected row into view within the panel whenever selection changes.
  useEffect(() => {
    selectedRowRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [navigation.selectedChangeId]);

  // Sync local input if parent clears searchTerm (e.g. resetFilters).
  useEffect(() => {
    setLocalSearch(searchTerm);
  }, [searchTerm]);

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setLocalSearch(value);
      if (debounceRef.current !== null) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        onSearchTermChange(value);
      }, 300);
    },
    [onSearchTermChange],
  );

  // Clean up debounce timer on unmount.
  useEffect(() => {
    return () => {
      if (debounceRef.current !== null) clearTimeout(debounceRef.current);
    };
  }, []);

  const countLabel = isFiltering
    ? `${changes.length}/${allChangesCount} 处变更`
    : `${changes.length} 处变更`;

  return (
    <>
      {/* Collapsed strip */}
      {!panel.isOpen && <PanelToggleStrip onOpen={panel.open} />}

      {/* Panel */}
      <div
        aria-label="变更列表"
        className={`flex h-full w-80 shrink-0 flex-col border-r border-ink-200 bg-ink-0 ${
          panel.isOpen ? '' : 'hidden'
        }`}
      >
        {/* Header */}
        <div className="flex flex-none items-center justify-between border-b border-ink-200 px-4 py-3">
          <div>
            <p className="text-sm font-semibold text-ink-900">变更列表</p>
            <p className="mt-0.5 text-xs text-ink-500">{countLabel}</p>
          </div>
          <button
            type="button"
            aria-label="折叠变更列表"
            onClick={panel.close}
            className="flex h-6 w-6 items-center justify-center rounded text-ink-400 hover:bg-ink-100 hover:text-ink-700"
          >
            <span className="select-none text-sm" aria-hidden="true">‹</span>
          </button>
        </div>

        {/* Search */}
        <div className="flex-none border-b border-ink-100 px-3 py-2">
          <div className="relative flex items-center">
            <span
              className="pointer-events-none absolute left-2 text-xs text-ink-400"
              aria-hidden="true"
            >
              &#x1F50D;
            </span>
            <input
              type="search"
              aria-label="搜索变更内容"
              placeholder="搜索变更内容…"
              value={localSearch}
              onChange={handleSearchChange}
              data-testid="change-search-input"
              className="h-8 w-full rounded-sm border border-ink-200 bg-ink-50 pl-7 pr-3 text-xs text-ink-900 placeholder-ink-400 focus:border-brand-400 focus:outline-none focus:ring-1 focus:ring-brand-400"
            />
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {changes.length === 0 && isFiltering ? (
            <p className="px-4 py-6 text-center text-xs text-ink-500">无匹配变更</p>
          ) : (
            <ol className="py-1">
              {changes.map((change, index) => {
                const isSelected = navigation.selectedChangeId === change.id;
                const status = decisions?.getStatus(change.id) ?? 'pending';
                const onSelect = () => navigation.select(change.id);
                return (
                  <ChangeListRow
                    key={change.id}
                    change={change}
                    indexLabel={index + 1}
                    isSelected={isSelected}
                    status={status}
                    searchTerm={searchTerm}
                    selectedRef={selectedRowRef}
                    onSelect={onSelect}
                  />
                );
              })}
            </ol>
          )}
        </div>
      </div>
    </>
  );
}

interface ChangeListRowProps {
  change: ChangeResponse;
  indexLabel: number;
  isSelected: boolean;
  status: DecisionStatus;
  searchTerm: string;
  /** Parent's mutable ref — the selected row mirrors its local DOM node here
   *  so the auto-scroll effect in ChangeListPanel can scrollIntoView it.
   *  Passed unconditionally to every row; the row decides via `isSelected`
   *  whether to write into it (see the effect below). */
  selectedRef: React.MutableRefObject<HTMLLIElement | null>;
  onSelect: () => void;
}

/**
 * Memo'd row with default shallow compare. The parent ChangeListPanel
 * precomputes `isSelected` / `status` / `onSelect` per render, so each row
 * only re-renders when its own state actually changed (e.g. this row became
 * the selected one, or this row's decision status flipped). Other rows —
 * the vast majority on any click — are skipped entirely.
 *
 * ref handling (P2-13): we hand the `<li>` a plain RefObject (`localRef`)
 * rather than a callback ref. The previous callback-ref implementation got
 * invoked twice on mount under React 18 StrictMode (node → null → node),
 * which is harmless for RefObject writes but deviates from best practice.
 * The `useEffect` below mirrors `localRef.current` into the parent's
 * `selectedRef` when `isSelected` flips, and clears it again when this row
 * is deselected — equivalent to the old callback behaviour but without the
 * StrictMode double-invocation.
 */
const ChangeListRow = memo(function ChangeListRow({
  change,
  indexLabel,
  isSelected,
  status,
  searchTerm,
  selectedRef,
  onSelect,
}: ChangeListRowProps): React.JSX.Element {
  const rawSummary = change.contentB ?? change.contentA ?? '';
  const summary = truncate(rawSummary, 60);
  const localRef = useRef<HTMLLIElement | null>(null);

  // Mirror our local DOM node into the parent's RefObject iff this row is
  // the currently-selected one. Deselecting clears the parent ref iff it
  // still points at us (defensive — guards against races where another row
  // already overwrote it).
  useEffect(() => {
    const node = localRef.current;
    if (isSelected && node) {
      selectedRef.current = node;
    } else if (!isSelected && selectedRef.current === node) {
      selectedRef.current = null;
    }
  }, [isSelected, selectedRef]);

  return (
    <li
      ref={localRef}
      onClick={onSelect}
      className={`relative flex cursor-pointer items-start gap-3 px-4 py-3 transition-colors duration-fast ${
        isSelected
          ? 'border-l-2 border-brand-500 bg-brand-50'
          : 'border-l-2 border-transparent hover:bg-ink-50'
      }`}
    >
      {/* Type color bar */}
      <span
        className={`mt-0.5 h-full min-h-[20px] w-0.5 flex-none self-stretch rounded-full ${CHANGE_TYPE_BAR[change.changeType]}`}
        aria-hidden="true"
      />

      {/* Content */}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-ink-700">
            #{indexLabel}
          </span>
          <span className="text-xs text-ink-400">
            {CHANGE_TYPE_LABELS[change.changeType]}
          </span>
          {status === 'accepted' && (
            <span className="text-xs font-medium text-semantic-success">
              已接受
            </span>
          )}
          {status === 'rejected' && (
            <span className="text-xs font-medium text-semantic-danger">
              已拒绝
            </span>
          )}
        </div>
        {summary && (
          <p className="mt-1 line-clamp-2 text-xs text-ink-600">
            <HighlightedText text={summary} term={searchTerm} />
          </p>
        )}
      </div>
    </li>
  );
});
